import { AddMetricButton, Button, IconButton } from '@ui/shared/Button';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import reject from 'lodash/reject';
import uniqBy from 'lodash/uniqBy';
import * as React from 'react';
import {
  ICrossTestMetricSelection,
  ISelectableEntity,
  ISelectableMetric,
  ISelectableTag,
  ISelectableTestType,
} from '../../../../data/types';
import { KEYS } from '../../../../keys';
import { ScrollableArea } from '../../../layout/ScrollableArea';
import { CardHeader, CardHeaderLeft } from '../../../shared/card/CardHeader';
import { Count } from '../../../shared/Count';
import { ControlLabel } from '../../../shared/form/ControlLabel';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { ListBulkOperation } from '../../../shared/form/ListBulkOperation';
import { ListControlLabel } from '../../../shared/form/ListControlLabel';
import { SingleMetricSelect } from '../../../shared/form/selects/SingleMetricSelect';
import { TagSelect } from '../../../shared/form/selects/TagsSelect';
import { TestTypeSelect } from '../../../shared/form/selects/TestTypeSelect';
import { Step } from '../../../shared/form/Step';
import { Icon } from '../../../shared/Icon';
import { ListControlContainer } from '../../../shared/List/ListControlContainer';
import { SecondaryText } from '../../../shared/SecondaryText';
import { FormSection } from './FormSection';
import { MetricList } from './MetricList';

interface IProps {
  metrics: ICrossTestMetricSelection[];
  onMetricsChange: (metrics: ICrossTestMetricSelection[]) => void;
  step?: number;
  max?: number;
  min?: number;
  useCutoffs?: boolean;
  unsignedCutoffs?: boolean;
  isBlockReport?: boolean;
  excludeAsymmetry?: boolean;
  onlyAsymmetry?: boolean;
  tagLabel?: string;
}

export const MetricSelector = (props: IProps): React.ReactElement => {
  const [testType, setTestType] = React.useState<ISelectableTestType>();
  const [tags, setTags] = React.useState<ISelectableTag[]>();
  const [metric, setMetric] = React.useState<ISelectableMetric>();

  /**
   * @description handles change to test type
   * @param s
   */
  const testTypeChangeHandler = (
    s: ISelectableTestType | ISelectableTestType[]
  ): void => {
    setTestType(s as ISelectableTestType);
    setMetric(null);
  };

  /**
   * @description handles change to tags
   * @param sTags
   * @returns
   */
  const tagsChangeHandler = (sTags: ISelectableTag[]): void => setTags(sTags);

  /**
   * @descirption handles change to metric
   * @param m
   * @returns
   */

  const metricChangeHandler = (m: ISelectableMetric): void => setMetric(m);

  /**
   * @description handles user adding the metric
   */
  const addMetric = (): void => {
    const metricSelection: ICrossTestMetricSelection = {
      changeCutoffs: {
        lowerCutoff: props.unsignedCutoffs ? 10 : -10,
        upperCutoff: props.unsignedCutoffs ? 15 : 10,
      },
      testType,
      tags: tags ?? [],
      metric,
    };
    // prevent adding duplicate metrics
    const mMetrics = [...props.metrics, metricSelection];
    const newMetrics = uniqBy(
      mMetrics,
      (selection: ICrossTestMetricSelection): string =>
        `${selection.testType.value}:${selection.metric.value}:${selection.tags
          ?.map((t: ISelectableEntity): string => t.value)
          .join(',')}`
    );
    props.onMetricsChange(newMetrics);
  };

  /**
   * @description handles removing a metric
   * @param metricSelection
   */
  const handleRemoveMetricSelection = (
    metricSelection: ICrossTestMetricSelection | ISelectableMetric
  ): void => {
    const newMetrics = reject(
      props.metrics,
      (selection: ICrossTestMetricSelection): boolean => {
        return isEqual(selection, metricSelection);
      }
    );
    props.onMetricsChange(newMetrics);
  };

  /**
   * @description handles changes to the lower % change value
   * @param metricSelection
   */
  const handleLowerCutoffChange = (
    metricSelection: ICrossTestMetricSelection
  ): void => {
    const metricArray = new Array(...props.metrics);
    const newLowerCutoff = metricSelection.changeCutoffs.lowerCutoff;

    const metricToChange = find(
      metricArray,
      (item: ICrossTestMetricSelection): boolean => {
        return (
          item.queryHash === metricSelection.queryHash &&
          item.metric.value === metricSelection.metric.value
        );
      }
    );

    const checkOverlap = (): number => {
      if (newLowerCutoff >= metricToChange.changeCutoffs.upperCutoff) {
        return metricToChange.changeCutoffs.upperCutoff;
      }
      return newLowerCutoff;
    };

    metricToChange.changeCutoffs = {
      ...metricToChange.changeCutoffs,
      lowerCutoff: checkOverlap(),
    };
    props.onMetricsChange(metricArray);
  };

  /**
   * @description handles changes to the upper % change value
   * @param metricSelection
   */
  const handleUpperCutoffChange = (
    metricSelection: ICrossTestMetricSelection
  ): void => {
    const metricArray = new Array(...props.metrics);
    const newUpperCutoff = metricSelection.changeCutoffs.upperCutoff;

    const metricToChange = find(
      metricArray,
      (item: ICrossTestMetricSelection): boolean => {
        return (
          item.queryHash === metricSelection.queryHash &&
          item.metric.value === metricSelection.metric.value
        );
      }
    );

    const checkOverlap = (): number => {
      if (newUpperCutoff <= metricToChange.changeCutoffs.lowerCutoff) {
        return metricToChange.changeCutoffs.lowerCutoff;
      }
      return newUpperCutoff;
    };

    metricToChange.changeCutoffs = {
      ...metricToChange.changeCutoffs,
      upperCutoff: checkOverlap(),
    };
    props.onMetricsChange(metricArray);
  };

  /**
   * @description handles clearing all metrics
   */
  const clearMetricsHandler = (): void => props.onMetricsChange([]);

  const reorderHandler = (
    mMetrics: Array<ICrossTestMetricSelection | ISelectableMetric>
  ): void => {
    props.onMetricsChange(mMetrics as ICrossTestMetricSelection[]);
  };

  return (
    <React.Fragment>
      <FormSection>
        <CardHeader>
          <CardHeaderLeft>
            {props.step && <Step>{props.step}</Step>}
            <div>
              <h6>Add Metrics</h6>
              {props.max && props.min ? (
                <SecondaryText>{`Please select a minimum of ${props.min} and a maximum of ${props.max}`}</SecondaryText>
              ) : props.max ? (
                <SecondaryText>{`Please select a maximum of ${props.max}`}</SecondaryText>
              ) : null}
            </div>
          </CardHeaderLeft>
        </CardHeader>
        <div style={{ padding: '10px' }}>
          <ControlSpacer>
            <ControlLabel>Test Type</ControlLabel>
            <TestTypeSelect
              value={testType}
              onChange={testTypeChangeHandler}
              disabled={false}
              id={KEYS.METRIC_SELECTOR_TEST_TYPE_SELECT}
            />
          </ControlSpacer>
          <ControlSpacer>
            <ControlLabel>{props.tagLabel ?? 'Tags'}</ControlLabel>
            <TagSelect
              value={tags}
              onChange={tagsChangeHandler}
              disabled={!testType}
              id={KEYS.METRIC_SELECTOR_TAGS_SELECT}
            />
          </ControlSpacer>
          <ControlSpacer>
            <ControlLabel>Metric</ControlLabel>
            <SingleMetricSelect
              testTypeId={testType?.value}
              disabled={!testType}
              value={metric}
              onChange={metricChangeHandler}
              excludeAsymmetry={props.excludeAsymmetry}
              onlyAsymmetry={props.onlyAsymmetry}
              id={KEYS.METRIC_SELECTOR_METRIC_SELECT}
            />
          </ControlSpacer>
          {props.isBlockReport && <BlockReportHelptext />}
          <ControlSpacer>
            <AddMetricButton
              onClick={addMetric}
              disabled={
                !testType || !metric || props?.max === props.metrics.length
              }
              data-testid={KEYS.METRIC_SELECTOR_ADD_METRIC_BUTTON}
            />
          </ControlSpacer>
        </div>
      </FormSection>

      <ListControlLabel label='Metrics'>
        <Button
          icon='cancel'
          title='Clear All'
          flavor='none'
          onClick={clearMetricsHandler}
          disabled={props.metrics.length === 0}
          style={{ padding: '5px', minHeight: 'unset' }}
        />
        <Count value={props.metrics?.length} />
      </ListControlLabel>
      <ListControlContainer>
        {props.isBlockReport && <BlockReportCutoffExplanation />}
        <ScrollableArea
          style={{
            flex: 1,
          }}
        >
          {props.metrics?.length > 0 && (
            <MetricList
              onReorder={reorderHandler}
              metrics={props.metrics}
              isBlockReport={props.isBlockReport}
              onRemoveSelection={handleRemoveMetricSelection}
              useCutoffs={props.useCutoffs}
              unsignedCutoffs={props.unsignedCutoffs}
              onLowerCutoffChange={handleLowerCutoffChange}
              onUpperCutoffChange={handleUpperCutoffChange}
            />
          )}
        </ScrollableArea>
      </ListControlContainer>
    </React.Fragment>
  );
};

const BlockReportCutoffExplanation = (): React.ReactElement => {
  return (
    <em
      style={{
        borderBottom: '1px solid var(--color-text)',
        padding: '2px 8px',
      }}
    >
      Use the red and green boxes to set % change cutoffs for traffic light
      flags on your report. Green represents the start of the best acceptable
      change in performance, and red the start of the worst change in
      performance. Values between red and green will be represented as yellow.
      An overlap between the two cutoffs will default the two values to the same
      number.
    </em>
  );
};

const BlockReportHelptext = (): React.ReactElement => {
  return (
    <ControlSpacer>
      <p style={{ lineHeight: '1.5em' }}>
        <Icon name='south' style={{ float: 'left', marginRight: '5px' }} />
        {
          'denotes metrics where negative values correlate to stronger performance, and in the list below, a green % change cutoff box will show to the left of the red. For example, you might specify a green value of -10 for Avg. Braking Power, meaning that this measure improved by 10% over the duration of the date block.'
        }
      </p>
    </ControlSpacer>
  );
};
